import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { ThemeProvider } from "@charisol-spark/ui";
import { clearTokens, createQueryClient } from "@charisol-spark/client-core";
import { ApiClientError } from "@/lib/api";

/**
 * Global handler for `ADMIN_MFA_REQUIRED` 403s.
 *
 * The admin router's MFA gate (see docs/runbooks/admin-mfa.md) can reject
 * ANY `/admin/*` request the instant the presented access token has no
 * record of a completed TOTP challenge — not just one specific mutation.
 * That happens when ADMIN_MFA_REQUIRED is on for this stage and either this
 * admin's session predates/outlived its MFA proof (the token was refreshed
 * — see mfa-store.ts — or it signed in before enforcement turned on), or
 * this admin was never enrolled at all (though AdminMfaGate exists
 * specifically so that case is caught before any admin page, and so this
 * call, ever runs). Either way the fix is the same: sign in again with a
 * TOTP code, not a generic error screen. React Query's per-cache `onError`
 * is the one place that sees every query AND mutation failure regardless of
 * which page triggered it, so that's where this is wired rather than
 * duplicated into every page's own error handling.
 */
export function handleQueryError(error: unknown): void {
  if (error instanceof ApiClientError && error.code === "ADMIN_MFA_REQUIRED") {
    clearTokens();
    if (window.location.pathname !== "/login") {
      window.location.href = "/login?reason=mfa_required";
    }
  }
}

/**
 * Module-level React Query client — one per app load, same pattern as
 * packages/frontend/src/app/providers.tsx, built from
 * @charisol-spark/client-core's factory (see that package's index.ts for why
 * it's a factory rather than a shared singleton).
 */
const queryClient = createQueryClient(handleQueryError);

export function Providers({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider defaultTheme="system" storageKey="spark-admin-theme">
          {children}
          <Toaster position="top-center" richColors closeButton />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
